import {
  Box,
  Circle,
  Flex,
  HStack,
  IconButton,
  Spacer,
  Text,
  createListCollection,
} from "@chakra-ui/react";
import * as React from "react";
import { useEffect } from "react";
import {
  LuLayoutDashboard,
  LuLifeBuoy,
  LuLock,
  LuLogOut,
  LuPlug,
  LuSettings,
} from "react-icons/lu";
import { Outlet, useLocation, useMatch, useNavigate } from "react-router-dom";

import AppShell from "../components/app-shell";
import { BreadcrumbsProvider } from "../components/breadcrumbs";
import { EmptyState } from "../components/empty-state";
import IconCircle from "../components/icon-circle";
import { Logo } from "../components/logo";
import NavItem from "../components/nav-item";
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
} from "../components/select";
import Sidebar from "../components/sidebar";
import SidebarSection from "../components/sidebar-section";
import { Toaster } from "../components/toaster";
import TopNav from "../components/top-nav";
import { useAppDispatch, useAppSelector, useWorkspaceURL } from "../hooks";
import { brand } from "../lib/brand";
import { checkUser } from "../store/authSlice";
import { selectCurrentWorkspace } from "../store/workspaceSlice";

export default function Root() {
  const navigate = useNavigate();
  const location = useLocation();
  const workspaceURL = useWorkspaceURL();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const workspace = useAppSelector(selectCurrentWorkspace);
  const currentSlug = workspace.slug;
  const isActive = workspace["active?"];
  useEffect(() => {
    if (user === null) {
      navigate("/login");
      return;
    }
    if (brand.id === "addie" && !workspace?.["onboarding-complete?"]) {
      navigate(`/workspaces/${workspace.slug}/onboarding`);
    }
  }, [navigate, user, workspace]);

  const scrollRef = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollRef.current?.scrollTo(0, 0);
  }, [location.pathname]);

  // Poll while the workspace is inactive so the page unlocks as soon
  // as an admin activates it.
  useEffect(() => {
    if (isActive) return;
    const interval = setInterval(() => {
      dispatch(checkUser());
    }, 5000);
    return () => clearInterval(interval);
  }, [dispatch, isActive]);

  const settingsExact = useMatch("/workspaces/:workspace/settings");
  const settingsNested = useMatch("/workspaces/:workspace/settings/*");
  const integrationsMatch = useMatch(
    "/workspaces/:workspace/settings/integrations/*",
  );
  const integrationsExact = useMatch(
    "/workspaces/:workspace/settings/integrations",
  );
  const integrationsActive = Boolean(integrationsMatch || integrationsExact);
  const dashboardActive = !settingsExact && !settingsNested;

  const workspaces = React.useMemo(() => {
    const workspaces = user?.workspaces ?? [];
    return createListCollection({
      items: workspaces.map((ws) => ({ value: ws.slug, label: ws.name })),
    });
  }, [user]);
  const handleWorkspaceChange = (e: { value: string[] }) => {
    const slug = e.value[0];
    navigate(`/workspaces/${slug}`);
  };

  const username = user?.username ?? "";
  const initial = username.charAt(0).toUpperCase();

  return (
    <BreadcrumbsProvider>
      <AppShell>
        <HStack align="stretch" flex="1" gap="0">
          <Sidebar>
            <Flex align="center" height="7" mt="8" paddingLeft="6">
              <Logo height="7" />
            </Flex>
            <Box mt="6" paddingX="4">
              <SelectRoot
                value={currentSlug ? [currentSlug] : []}
                onValueChange={handleWorkspaceChange}
                collection={workspaces}
                size="sm"
                variant="workspace"
              >
                <SelectTrigger>
                  <SelectValueText />
                </SelectTrigger>
                <SelectContent>
                  {workspaces.items.map((ws) => (
                    <SelectItem key={ws.value} item={ws}>
                      {ws.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </SelectRoot>
            </Box>
            <SidebarSection gap="1.5" mt="4" paddingX="4">
              <NavItem
                icon={<LuLayoutDashboard />}
                to={workspaceURL("")}
                active={dashboardActive}
              >
                Dashboard
              </NavItem>
              <NavItem
                icon={<LuPlug />}
                to={workspaceURL("settings/integrations")}
                active={integrationsActive}
              >
                Integrations
              </NavItem>
            </SidebarSection>
            <Spacer />
            <Flex direction="column" gap="6" paddingBottom="8" paddingX="4">
              <SidebarSection gap="1.5" p="0">
                {brand.id === "addie" && (
                  <NavItem
                    icon={<LuLifeBuoy />}
                    href="https://addie.gitbook.io/addie-docs/getting-started"
                    external
                  >
                    Helpdesk
                  </NavItem>
                )}
                <NavItem
                  icon={<LuSettings />}
                  to={workspaceURL("settings")}
                  active={Boolean(settingsExact)}
                >
                  Settings
                </NavItem>
              </SidebarSection>
              <Box background="white/20" height={"1px" as any} />
              <HStack gap="2" justify="space-between" paddingX="2">
                <HStack gap="3" minWidth="0">
                  <Circle
                    background="white/10"
                    color="white"
                    fontFamily="inter"
                    fontSize="sm"
                    fontWeight="semibold"
                    size="10"
                  >
                    {initial}
                  </Circle>
                  <Text
                    color="white"
                    fontFamily="inter"
                    fontSize="sm"
                    fontWeight="semibold"
                    minWidth="0"
                    truncate
                  >
                    {username}
                  </Text>
                </HStack>
                <IconButton
                  aria-label="Log out"
                  asChild
                  color="white/70"
                  size="xs"
                  variant="ghost"
                  _hover={{ background: "white/8", color: "white" }}
                >
                  <a href="/logout" title="Log out">
                    <LuLogOut size="16" />
                  </a>
                </IconButton>
              </HStack>
            </Flex>
          </Sidebar>
          <Flex
            direction="column"
            flex="1"
            minWidth="0"
            overflowY="auto"
            ref={scrollRef}
          >
            <TopNav />
            <Flex
              direction="column"
              flex="1"
              gap="6"
              paddingBottom="8"
              paddingTop="4"
              paddingX="6"
            >
              {isActive ? (
                <Outlet />
              ) : (
                <HStack alignItems="center" flex="1">
                  <EmptyState
                    icon={<IconCircle Icon={LuLock} size="md" variant="flat" />}
                    title="Workspace not activated"
                    description="This workspace needs to be activated by an admin before you can use it."
                  />
                </HStack>
              )}
            </Flex>
          </Flex>
        </HStack>
        <Toaster />
      </AppShell>
    </BreadcrumbsProvider>
  );
}
